import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Check, CircleDot, Circle, Undo2, ChevronRight, Layers } from "lucide-react";
import { toast } from "sonner";

const CURRENT_FY = (() => {
  const now = new Date();
  const y = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
})();
const FY_OPTIONS = [-2, -1, 0, 1].map((d) => {
  const y = parseInt(CURRENT_FY.split("-")[0], 10) + d;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
}).reverse();

const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

export default function ComplianceTrackerTab({ clientId }) {
  const { user } = useAuth();
  const canEdit = ["Admin", "Manager", "Executive"].includes(user.role);
  const canDelete = ["Admin", "Manager"].includes(user.role);
  const [fy, setFy] = useState(CURRENT_FY);
  const [types, setTypes] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [noteDialog, setNoteDialog] = useState(null); // { period, stage_index }

  const load = async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        api.get(`/compliance-types`),
        api.get(`/clients/${clientId}/compliance-periods`, { params: { financial_year: fy } }),
      ]);
      setTypes(t.data || []);
      setPeriods(p.data || []);
    } catch (e) {
      toast.error("Failed to load compliance");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, fy]);

  const removePeriod = async (p) => {
    if (!window.confirm(`Delete this ${p.type_name} — ${p.period_label} period?`)) return;
    await api.delete(`/compliance-periods/${p.id}`);
    toast.success("Deleted"); load();
  };
  const completeStage = async (period, stage_index, note) => {
    try {
      await api.post(`/compliance-periods/${period.id}/complete-stage`, { stage_index, note });
      toast.success("Stage completed"); load();
      setNoteDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete stage");
    }
  };
  const reopenStage = async (period, stage_index) => {
    const reason = window.prompt(`Reopen stage #${stage_index + 1}? This will undo all later stages. Reason (optional):`);
    if (reason === null) return;
    try {
      await api.post(`/compliance-periods/${period.id}/reopen-stage`,
                     { stage_index, note: reason });
      toast.success("Stages reopened"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reopen");
    }
  };

  const shown = periods.filter((p) => {
    if (typeFilter !== "all" && p.type_id !== typeFilter) return false;
    if (stateFilter === "complete" && !p.is_complete) return false;
    if (stateFilter === "in_progress" && (p.is_complete || (p.completed_stages_count || 0) === 0)) return false;
    if (stateFilter === "not_started" && (p.completed_stages_count || 0) > 0) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="compliance-tracker-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Compliance Tracker</h3>
          <p className="text-xs text-slate-500 mt-0.5">Stage-workflow tracking for GST, TDS, ITR and other recurring compliances.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="comp-fy"><SelectValue /></SelectTrigger>
            <SelectContent>{FY_OPTIONS.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="comp-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="comp-state-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              <SelectItem value="not_started">Not started</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="complete">Completed</SelectItem>
            </SelectContent>
          </Select>
          {canEdit && (
            <>
              <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="btn-bulk-compliance">
                <Layers size={12} className="mr-1.5" /> Bulk create for FY
              </Button>
              <Button size="sm" onClick={() => setDialogOpen(true)} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="btn-new-compliance">
                <Plus size={12} className="mr-1.5" /> New period
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-slate-300 rounded-sm">
          <p className="text-sm text-slate-500">No compliance periods for these filters.</p>
          {canEdit && (
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setDialogOpen(true)}>
              <Plus size={12} className="mr-1.5" /> Add a compliance period
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {shown.map((p) => (
            <PeriodCard key={p.id} period={p} canEdit={canEdit} canDelete={canDelete}
                        onComplete={(idx) => setNoteDialog({ period: p, stage_index: idx })}
                        onReopen={(idx) => reopenStage(p, idx)}
                        onDelete={() => removePeriod(p)} />
          ))}
        </div>
      )}

      <NewComplianceDialog open={dialogOpen} onOpenChange={setDialogOpen} clientId={clientId}
                           types={types} fy={fy} onDone={load} />
      <BulkCreateDialog open={bulkOpen} onOpenChange={setBulkOpen} clientId={clientId}
                        types={types} fy={fy} onDone={load} />
      <StageNoteDialog noteDialog={noteDialog} onClose={() => setNoteDialog(null)}
                       onConfirm={(note) => completeStage(noteDialog.period, noteDialog.stage_index, note)} />
    </div>
  );
}

function PeriodCard({ period, canEdit, canDelete, onComplete, onReopen, onDelete }) {
  const stages = period.stages || [];
  const currentIdx = period.current_stage_index ?? 0;
  const total = stages.length;
  const completed = period.completed_stages_count || 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="border border-slate-200 rounded-sm bg-white" data-testid={`comp-period-${period.id}`}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[#0a2540]">{period.type_name}</span>
            <span className="text-[10px] uppercase tracking-wide bg-slate-100 px-1.5 py-0.5 rounded-sm text-slate-600 font-semibold">
              {period.period_label}
            </span>
            <span className="text-[10px] text-slate-400">FY {period.financial_year}</span>
            {period.is_complete && (
              <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-sm font-semibold">
                Complete
              </span>
            )}
          </div>
          {/* Progress bar */}
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${period.is_complete ? "bg-emerald-500" : "bg-[#0a2540]"}`}
                   style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] text-slate-500 font-mono-data tabular-nums">
              {completed}/{total}
            </span>
          </div>
        </div>
        {canDelete && (
          <button onClick={onDelete} className="text-slate-300 hover:text-red-600 mt-1">
            <Trash2 size={12} />
          </button>
        )}
      </div>
      <ol className="p-3 space-y-1.5">
        {stages.map((s, i) => {
          const done = i < completed;
          const isCurrent = i === currentIdx && !period.is_complete;
          return (
            <li key={i}
                className={`flex items-center gap-2 rounded-sm px-2 py-1.5 border ${
                  done ? "bg-emerald-50 border-emerald-100"
                       : isCurrent ? "bg-amber-50 border-amber-200"
                                    : "bg-slate-50 border-slate-100"
                }`}
                data-testid={`stage-${period.id}-${i}`}>
              {done ? <Check size={13} className="text-emerald-600 flex-shrink-0" />
                    : isCurrent ? <CircleDot size={13} className="text-amber-600 flex-shrink-0" />
                                : <Circle size={13} className="text-slate-300 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className={`text-xs ${done ? "text-slate-600 line-through" : isCurrent ? "font-semibold" : "text-slate-500"}`}>
                  {s.name}
                </div>
                {done && s.completed_by_name && (
                  <div className="text-[9px] text-slate-400">
                    ✓ {s.completed_by_name}
                    {s.completed_at && ` · ${new Date(s.completed_at).toLocaleString("en-IN", {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                    })}`}
                    {s.note && <span className="italic"> · {s.note}</span>}
                  </div>
                )}
              </div>
              {isCurrent && canEdit && (
                <button onClick={() => onComplete(i)}
                        className="text-[10px] font-semibold px-2 py-1 bg-[#0a2540] text-white rounded-sm hover:opacity-90 inline-flex items-center gap-1"
                        data-testid={`btn-complete-${period.id}-${i}`}>
                  Complete <ChevronRight size={11} />
                </button>
              )}
              {done && canEdit && i === completed - 1 && (
                <button onClick={() => onReopen(i)} className="text-[9px] text-slate-400 hover:text-amber-600 inline-flex items-center gap-0.5"
                        data-testid={`btn-reopen-${period.id}-${i}`}>
                  <Undo2 size={10} /> Reopen
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function NewComplianceDialog({ open, onOpenChange, clientId, types, fy, onDone }) {
  const [type_id, setTypeId] = useState("");
  const [period_label, setPeriodLabel] = useState("");
  const [financial_year, setFy] = useState(fy);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setFy(fy); }, [fy]);
  useEffect(() => {
    if (!type_id && types.length) setTypeId(types[0].id);
  }, [types, type_id]);

  const type = types.find((t) => t.id === type_id);
  const freq = type?.default_frequency || "monthly";
  const monthOpts = MONTHS.map((m, i) => {
    const yr = parseInt(financial_year.split("-")[0], 10) + (i < 9 ? 0 : 1);
    return `${m} ${yr}`;
  });
  const quarterOpts = ["Q1 (Apr-Jun)", "Q2 (Jul-Sep)", "Q3 (Oct-Dec)", "Q4 (Jan-Mar)"];
  const periodOpts = freq === "quarterly" ? quarterOpts
    : freq === "annual" ? [`FY ${financial_year}`]
    : monthOpts;

  useEffect(() => {
    if (periodOpts.length && !periodOpts.includes(period_label)) setPeriodLabel(periodOpts[0]);
  // eslint-disable-next-line
  }, [type_id, financial_year]);

  const submit = async () => {
    if (!type_id || !period_label) return toast.error("Type and period required");
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/compliance-periods`, {
        type_id, period_label, financial_year, notes: notes.trim() || null,
      });
      toast.success("Compliance period created");
      setNotes(""); onOpenChange(false); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-new-compliance">
        <DialogHeader><DialogTitle>New compliance period</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Compliance type</Label>
            <Select value={type_id} onValueChange={setTypeId}>
              <SelectTrigger data-testid="comp-type"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.stages?.length || 0} stages)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Financial year</Label>
              <Select value={financial_year} onValueChange={setFy}>
                <SelectTrigger data-testid="comp-fy-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FY_OPTIONS.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Period</Label>
              <Select value={period_label} onValueChange={setPeriodLabel}>
                <SelectTrigger data-testid="comp-period"><SelectValue /></SelectTrigger>
                <SelectContent>{periodOpts.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="comp-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="comp-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkCreateDialog({ open, onOpenChange, clientId, types, fy, onDone }) {
  const [type_id, setTypeId] = useState("");
  const [financial_year, setFyLocal] = useState(fy);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setFyLocal(fy); }, [fy]);
  useEffect(() => {
    if (!type_id && types.length) setTypeId(types[0].id);
  }, [types, type_id]);

  const type = types.find((t) => t.id === type_id);
  const freq = type?.default_frequency || "monthly";
  const previewCount = freq === "monthly" ? 12 : freq === "quarterly" ? 4 : 1;
  const previewLabel = freq === "monthly" ? "12 monthly periods (Apr → Mar)"
    : freq === "quarterly" ? "4 quarterly periods (Q1 → Q4)"
    : "1 annual period";

  const submit = async () => {
    if (!type_id) return toast.error("Pick a compliance type");
    setSaving(true);
    try {
      const r = await api.post(`/clients/${clientId}/compliance-periods/bulk`,
                               { type_id, financial_year });
      const { created, skipped } = r.data;
      if (created === 0) {
        toast.info(`All ${skipped} periods already exist — nothing to do`);
      } else {
        toast.success(`Created ${created} period${created === 1 ? "" : "s"}`
          + (skipped ? ` · ${skipped} already existed` : ""));
      }
      onOpenChange(false); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk create failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-bulk-compliance">
        <DialogHeader>
          <DialogTitle>Bulk create compliance periods</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Materializes all periods for the chosen compliance type across the whole financial year in one click.
            Existing periods are skipped.
          </p>
          <div>
            <Label>Compliance type</Label>
            <Select value={type_id} onValueChange={setTypeId}>
              <SelectTrigger data-testid="bulk-type"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.default_frequency}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Financial year</Label>
            <Select value={financial_year} onValueChange={setFyLocal}>
              <SelectTrigger data-testid="bulk-fy"><SelectValue /></SelectTrigger>
              <SelectContent>{FY_OPTIONS.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {type && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs">
              <div className="text-slate-500">
                Will create <span className="font-semibold text-[#0a2540]">{previewCount}</span> periods:
                {" "}<span>{previewLabel}</span>
              </div>
              <div className="text-slate-500 mt-1">
                Each will have <span className="font-semibold">{type.stages?.length || 0}</span> stages to progress through.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !type_id}
                  className="bg-[#0a2540] hover:bg-[#0a2540]/90"
                  data-testid="bulk-submit">
            <Layers size={13} className="mr-1.5" /> Create {previewCount} period{previewCount === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StageNoteDialog({ noteDialog, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [noteDialog?.period?.id, noteDialog?.stage_index]);
  if (!noteDialog) return null;
  const stage = noteDialog.period.stages?.[noteDialog.stage_index];
  return (
    <Dialog open={!!noteDialog} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="dialog-stage-note">
        <DialogHeader><DialogTitle>Complete: {stage?.name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Note (optional)</Label>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Any relevant details…" data-testid="stage-note-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onConfirm(note.trim() || null)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="stage-note-confirm">
            <Check size={13} className="mr-1.5" /> Complete stage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
